import { Injectable, OnDestroy } from '@angular/core';

import {
  BehaviorSubject,
  concatMap,
  map,
  merge,
  mergeMap,
  Observable,
  of,
  filter,
  startWith,
  Subject,
  Subscription,
  take,
  zip,
} from 'rxjs';

import {
  FloorPlan,
  FloorPlanItem,
  FloorPlanObject,
  FloorPlanSection,
  FloorPlanTable,
  GroupedItems,
  TableAttributes,
} from '@shared/floor-plan/models';
import { Resource } from '@shared/resource/models';
import { Service } from '@shared/service/models';
import { FloorPlanItemType } from '@shared/floor-plan/enums';
import { AuthUser } from '@shared/signup/models';
import { FloorPlanForSwitcher, FloorPlanTableData } from '@tablesready/canvas';
import {
  FloorPlanItemApiService,
  ResourcesService,
  ServicesService,
} from './index';
import { FloorPlanService } from './floor-plan.service';
import { FloorPlanItemService } from './floor-plan-item.service';
import { AuthRestaurantService } from '@tablesready/frontend-store';

@Injectable()
export class FloorPlanTableService implements OnDestroy {
  savedTables: FloorPlanTable[] = [];
  savedObjects: FloorPlanObject[] = [];
  savedSections: FloorPlanSection[] = [];
  floorPlans: FloorPlanForSwitcher[] = [];
  resourcesMap = new Map<string, Resource>();
  resources$: Observable<Resource[]>;
  services: Service[] = [];
  loadingFloorPlanForNewLocation$: Observable<[number, number]>;
  floorPlanIdToTablesMap$: Observable<Record<string, FloorPlanTable[]>>;
  resourceIdToTableDataMap$: Observable<Record<string, FloorPlanTableData>>;
  floorPlans$: Observable<FloorPlan[]>;

  private floorPlanEnabled = false;
  private floorPlanIdItemsAreLoadedFor?: string;
  private readonly resourcesSource$ = new BehaviorSubject<Resource[]>([]);
  private readonly loadingFloorPlanForNewLocationSource$ = new Subject<[number, number]>();
  private readonly floorPlanIdToTableMapSource$ = new BehaviorSubject<Record<string, FloorPlanTable[]>>({});
  private readonly resourceIdToTableDataMapSource$ = new BehaviorSubject<Record<string, FloorPlanTableData>>({});
  private readonly floorPlansSource$ = new Subject<FloorPlan[]>();
  private readonly subscriptions = new Subscription();

  constructor(
    private readonly resourceService: ResourcesService,
    private readonly servicesService: ServicesService,
    private readonly floorPlanService: FloorPlanService,
    private readonly itemService: FloorPlanItemService,
    private readonly floorPlanItemApiService: FloorPlanItemApiService,
    private readonly authRestaurantService: AuthRestaurantService,
  ) {
    this.floorPlanIdToTablesMap$ = this.floorPlanIdToTableMapSource$.asObservable();
    this.resourceIdToTableDataMap$ = this.resourceIdToTableDataMapSource$.asObservable();
    this.loadingFloorPlanForNewLocation$ = this.loadingFloorPlanForNewLocationSource$.asObservable();
    this.floorPlans$ = this.floorPlansSource$.asObservable();
    this.resources$ = this.resourcesSource$.asObservable();

    const initialLoad = merge(
      this.floorPlanService.listenToChanges(),
      this.authRestaurantService.selectAndDetectLocationChanges(),
    )
      .pipe(
        startWith(this.authRestaurantService.get()),
        filter(response => {
          if ((response as AuthUser).account) {
            this.floorPlanEnabled = (response as AuthUser).account.features.floorPlan;
          }

          return this.floorPlanEnabled;
        }),
        concatMap(() => this.floorPlanService.load()),
        mergeMap((plans) => {
          if (plans.length === 0) {
            return of([]);
          }

          return zip(plans.map(plan => this.floorPlanItemApiService.getListForId(plan.id)
            .pipe(map(children => ({
              plan,
              children,
            })))));
        }),
      )
      .subscribe((items: Array<{ plan: FloorPlan; children: FloorPlanItem[] }>) => {
        this.floorPlanIdToTableMapSource$.next({});
        const floorPlans: FloorPlanForSwitcher[] = [];
        items.forEach(({ plan, children }) => {
          if (!children) {
            return;
          }

          const tables = children
            .filter(item => item.type === FloorPlanItemType.table)
            .map(table => ({
              ...table,
              ...table.attributes as TableAttributes,
            }));

          this.setTables(plan.id, tables as unknown as FloorPlanTable[]);
          floorPlans.push({
            ...plan,
            tablesCount: tables.length,
          });
        });

        this.floorPlans = floorPlans;
        this.floorPlansSource$.next(floorPlans);
        this.floorPlanIdItemsAreLoadedFor = undefined;

        this.loadingFloorPlanForNewLocationSource$.next([
          items.length,
          items.reduce((total, current) => total + current.children.length, 0),
        ]);
      });
    this.subscriptions.add(initialLoad);

    const itemsSub = merge(
      this.itemService.selectRaw(),
      this.itemService.listenToChanges(),
    )
      .subscribe(() => {
        this.floorPlanIdItemsAreLoadedFor = undefined;
      });
    this.subscriptions.add(itemsSub);

    const servicesSub = merge(
      this.servicesService.listenToChanges(),
      this.servicesService.selectAll(),
    )
      .pipe(concatMap(() => this.servicesService.selectAllEnabled()))
      .subscribe((services) => {
        this.services = services;
      });
    this.subscriptions.add(servicesSub);

    const resourceSub = merge(
      this.resourceService.listenToChanges(),
      this.resourceService.selectAll(),
    )
      // Do not change selectAll() to selectEnabled(). we need info about disabled resources as well!
      .pipe(concatMap(() => this.resourceService.selectAll()))
      .subscribe((resources) => {
        this.resourcesMap.clear();
        this.resourcesSource$.next(resources);
        resources.forEach((res) => {
          this.resourcesMap.set(res._id, res);
        });
      });
    this.subscriptions.add(resourceSub);
  }

  get resources(): Resource[] {
    return this.resourcesSource$.getValue();
  }

  get resourceIdToTableDataMap(): Record<string, FloorPlanTableData> {
    return this.resourceIdToTableDataMapSource$.getValue();
  }

  getFloorPlanNameByResourceId(resourceId: string): string {
    const data = this.resourceIdToTableDataMap[resourceId];

    if (!data) {
      return '';
    }

    const floorPlanName = this.floorPlans.find(plan => plan.id === data?.floorPlanId)?.name;

    return floorPlanName ? `[${floorPlanName}]` : '';
  }

  getResourceNameByIdWithFloorPlan(resourceId: string, currentFloorPlanId?: string): string {
    const resourceName = this.resourcesMap.get(resourceId)?.name;
    const data = this.resourceIdToTableDataMap[resourceId];

    if (!data) {
      return `${resourceName} [Not on Plan]`;
    }

    if (!currentFloorPlanId || data?.floorPlanId === currentFloorPlanId) {
      return resourceName;
    }

    const floorPlanName = this.floorPlans.find(plan => plan.id === data?.floorPlanId)?.name;

    return floorPlanName ? `${resourceName} [${floorPlanName}]` : resourceName;
  }

  getItems(floorPlanId?: string): Observable<GroupedItems> {
    if (!floorPlanId) {
      return of({
        tables: [],
        objects: [],
        sections: [],
      });
    }

    return of(floorPlanId).pipe(
      concatMap((planId) => {
        if (planId !== this.floorPlanIdItemsAreLoadedFor) {
          this.floorPlanIdItemsAreLoadedFor = planId;

          return this.itemService.load(planId);
        }

        return of(planId);
      }),
      concatMap(() => this.itemService.selectGrouped()),
      take(1),
      map((list) => {
        this.savedTables = list.tables;
        this.savedObjects = list.objects;
        this.savedSections = list.sections;

        const planIndexToEdit = this.floorPlans.findIndex(plan => plan.id === floorPlanId);
        this.floorPlans[planIndexToEdit].tablesCount = list.tables.length;
        this.setTables(floorPlanId, list.tables);

        return list;
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private setTables(floorPlanId: string, tables: FloorPlanTable[]): void {
    if (!floorPlanId) {
      return;
    }

    const newTables = { ...this.floorPlanIdToTableMapSource$.getValue() };
    newTables[floorPlanId] = tables;
    this.floorPlanIdToTableMapSource$.next(newTables);

    const tablesData: Record<string, FloorPlanTableData> = {};
    Object.entries(newTables).forEach(([planId, items]) => {
      items.forEach((table) => {
        tablesData[table.resourceId] = {
          floorPlanId: planId,
          resourceId: table.resourceId,
        };
      });
    });
    this.resourceIdToTableDataMapSource$.next(tablesData);
  }
}
